import parseDiff from 'parse-diff';

// Define the types that parse-diff uses internally
type AddChange = {
  type: 'add';
  add: true;
  ln: number;
  content: string;
};

type DeleteChange = {
  type: 'del';
  del: true;
  ln: number;
  content: string;
};

type NormalChange = {
  type: 'normal';
  normal: true;
  ln1: number;
  ln2: number;
  content: string;
};

type Change = AddChange | DeleteChange | NormalChange;

type Chunk = {
  content: string;
  changes: Change[];
  oldStart: number;
  oldLines: number;
  newStart: number;
  newLines: number;
};

type File = {
  chunks?: Chunk[];
  deletions: number;
  additions: number;
  from?: string;
  to?: string;
  new?: boolean;
  deleted?: boolean;
  index?: string[];
  binary?: boolean;
};

export type DiffFile = {
  oldPath: string;
  newPath: string;
  type: 'add' | 'delete' | 'modify' | 'rename';
  hunks: DiffHunk[];
  isBinary: boolean;
  stats: {
    additions: number;
    deletions: number;
  };
};

export type DiffHunk = {
  content: string;
  oldStart: number;
  oldLines: number;
  newStart: number;
  newLines: number;
  changes: DiffChange[];
};

export type DiffChange = {
  type: 'normal' | 'insert' | 'delete';
  content: string;
  // Line numbers are 1-based
  oldLineNumber?: number; // undefined for inserts
  newLineNumber?: number; // undefined for deletes
  // For normal lines, both line numbers are defined
  lineNumber?: number; // Used for single-side changes (insert/delete)
};

// Enhanced type to support expandable gaps
export type ProcessedHunk = DiffHunk | {
  type: 'gap';
  count: number;
  startOldLine: number;
  startNewLine: number;
  endOldLine: number;
  endNewLine: number;
  gapId: string;
} | {
  type: 'expanded-gap';
  contextLines: DiffChange[];
  gapId: string;
};

/**
 * Parse a git diff string into a structured format.
 * @param diffText The raw git diff text
 * @returns Array of parsed diff files
 */
export function parseGitDiff(diffText: string): DiffFile[] {
  const files = parseDiff(diffText);
  return files.map(transformParsedFile);
}

function transformParsedFile(file: File): DiffFile {
  // Count additions and deletions
  let additions = 0;
  let deletions = 0;

  // Transform hunks and count changes
  const hunks =
    file.chunks?.map((hunk) => {
      const changes = hunk.changes
        .map((change: Change): DiffChange => {
          // Skip "No newline at end of file" lines
          if (change.content.trim() === '\\ No newline at end of file') {
            // Return null to filter out later
            return null as unknown as DiffChange;
          }

          // Remove the leading +/- from the content but preserve indentation
          let content = change.content;
          if (content.startsWith('+') || content.startsWith('-')) {
            // Replace just the first + or - character but preserve all whitespace
            content = ' ' + content.substring(1);
          }

          if (change.type === 'add') {
            additions++;
            return {
              type: 'insert',
              content,
              lineNumber: change.ln,
              newLineNumber: change.ln,
            };
          } else if (change.type === 'del') {
            deletions++;
            return {
              type: 'delete',
              content,
              lineNumber: change.ln,
              oldLineNumber: change.ln,
            };
          } else {
            return {
              type: 'normal',
              content,
              oldLineNumber: change.ln1,
              newLineNumber: change.ln2,
            };
          }
        })
        .filter(Boolean) as DiffChange[]; // Filter out null entries

      return {
        content: hunk.content,
        oldStart: hunk.oldStart,
        oldLines: hunk.oldLines,
        newStart: hunk.newStart,
        newLines: hunk.newLines,
        changes,
      };
    }) ?? [];

  // Determine file type
  let type: DiffFile['type'] = 'modify';
  if (file.new) {
    type = 'add';
  } else if (file.deleted) {
    type = 'delete';
  } else if (file.to !== file.from) {
    type = 'rename';
  }

  // Handle paths
  let oldPath = file.from ?? '/dev/null';
  let newPath = file.to ?? '/dev/null';

  // Clean up paths (remove a/ and b/ prefixes if they exist)
  oldPath = oldPath.replace(/^[ab]\//, '');
  newPath = newPath.replace(/^[ab]\//, '');

  // For new files, oldPath should be /dev/null
  if (type === 'add') {
    oldPath = '/dev/null';
  }

  // For deleted files, newPath should be /dev/null
  if (type === 'delete') {
    newPath = '/dev/null';
  }

  // Check if this is a binary file - either explicitly marked as binary or has no chunks
  const isBinary = Boolean(file.binary) || (file.chunks?.length === 0 && file.deletions === 0 && file.additions === 0);

  // For binary files, we don't have hunks
  const processedHunks = isBinary ? [] : hunks;

  // For binary files, we don't count additions/deletions
  const stats = {
    additions: isBinary ? 0 : additions,
    deletions: isBinary ? 0 : deletions,
  };

  return {
    oldPath,
    newPath,
    type,
    hunks: processedHunks,
    isBinary,
    stats,
  };
}

/**
 * Utility function to get a unique identifier for a line.
 * This is used for selection tracking and comment association.
 */
export function getLineId(side: 'LEFT' | 'RIGHT', lineNumber: number): string {
  return `${side}:${lineNumber}`;
}

/**
 * Utility function to parse a line ID back into its components.
 */
export function parseLineId(lineId: string): { side: 'LEFT' | 'RIGHT'; lineNumber: number } {
  const [side, lineNumber] = lineId.split(':');
  return {
    side: side as 'LEFT' | 'RIGHT',
    lineNumber: parseInt(lineNumber, 10),
  };
}
